import { useState, useEffect, useRef } from 'react';
import { Card, Input, Button, Toast } from 'antd-mobile';
import { useStore } from '../store';
import { useTransactions } from '../hooks/useTransactions';
import { formatCurrency } from '@tangtung/shared';
import { useNavigate } from 'react-router-dom';
import liff from '@line/liff';
import tangtungMascot from '../assets/tangtung-settings.png';
import './QRPage.css';

// Local QR generation to avoid shared package issue
async function generatePromptPayQR(phoneNumber: string, amount: number): Promise<string> {
    const { default: generatePayload } = await import('promptpay-qr');
    const { toDataURL } = await import('qrcode');

    const cleanNumber = phoneNumber.replace(/[-\s]/g, '');
    const payload = generatePayload(cleanNumber, { amount });

    const qrDataUrl = await toDataURL(payload, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 300,
    });

    return qrDataUrl;
}

export default function QRPage() {
    const promptPay = useStore((s) => s.promptPay);
    const calcResult = useStore((s) => s.calcResult);
    const user = useStore((s) => s.user);
    const navigate = useNavigate();
    const { saveTransaction } = useTransactions();

    const [amount, setAmount] = useState(calcResult > 0 ? calcResult.toString() : '');
    const [qrDataUrl, setQrDataUrl] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [uploadingSlip, setUploadingSlip] = useState(false);
    const [savedTransactionId, setSavedTransactionId] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasPromptPay = promptPay.number && promptPay.number.length > 0;
    const numericAmount = parseFloat(amount) || 0;

    useEffect(() => {
        if (hasPromptPay && numericAmount > 0) {
            generatePromptPayQR(promptPay.number, numericAmount).then(setQrDataUrl);
        } else {
            setQrDataUrl('');
        }
    }, [promptPay.number, numericAmount, hasPromptPay]);

    const handleSave = async () => {
        if (numericAmount <= 0) return;

        setSaving(true);
        const result = await saveTransaction('qr', numericAmount);
        setSaving(false);

        if (result) {
            // Save transaction ID and open camera directly (no receipt message here, will send with slip)
            setSavedTransactionId(result.id);
            // Open camera immediately
            setTimeout(() => {
                fileInputRef.current?.click();
            }, 100);
        }
    };

    const handleCaptureSlip = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !savedTransactionId || !user?.userId) return;

        // Upload immediately after capture
        setUploadingSlip(true);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('line_user_id', user.userId);
            formData.append('transaction_id', savedTransactionId.toString());

            const uploadResponse = await fetch(
                'https://tangtungdev-api.greenslime2009.workers.dev/api/slips/upload',
                {
                    method: 'POST',
                    body: formData,
                }
            );

            if (!uploadResponse.ok) {
                throw new Error('Upload failed');
            }

            const uploadResult = await uploadResponse.json();

            // Send receipt with slip image to LINE (await to ensure it sends before closing)
            if (uploadResult.url) {
                await fetch('https://tangtungdev-api.greenslime2009.workers.dev/api/receipts/send', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        line_user_id: user.userId,
                        amount: numericAmount,
                        payment_type: 'qr',
                        transaction_id: savedTransactionId,
                        slip_url: uploadResult.url,
                    }),
                });
            }

            // Show success popup then close LIFF
            Toast.show({
                content: 'บันทึกสลิปสำเร็จ',
                icon: 'success',
                duration: 1500,
                afterClose: () => {
                    // Close LIFF to go back to LINE chat
                    if (liff.isInClient()) {
                        liff.closeWindow();
                    } else {
                        navigate('/');
                    }
                }
            });
        } catch (error) {
            console.error('Error uploading slip:', error);
            Toast.show({ content: 'อัปโหลดสลิปไม่สำเร็จ', icon: 'fail' });
        } finally {
            setUploadingSlip(false);
        }
    };


    if (!hasPromptPay) {
        return (
            <div className="page qr-page">
                <div className="empty-state">
                    <img
                        src={tangtungMascot}
                        alt="น้องตังค์ตุง"
                        className="qr-mascot-image"
                    />
                    <h2 className="empty-title">ยังไม่ได้ตั้งค่า PromptPay</h2>
                    <p className="empty-description">กรุณาตั้งค่าเบอร์ PromptPay ในหน้าโปรไฟล์ก่อน</p>
                    <Button
                        block
                        color="primary"
                        size="large"
                        onClick={() => navigate('/profile?edit=promptpay')}
                        className="setup-btn"
                    >
                        ไปตั้งค่า
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="page qr-page">
            <div className="page-content">
                {/* PromptPay Info */}
                <Card className="promptpay-info-card">
                    <div className="promptpay-info">
                        <span className="label">PromptPay</span>
                        <span className="name">{promptPay.name || 'ไม่ระบุชื่อ'}</span>
                        <span className="number">{promptPay.number}</span>
                    </div>
                </Card>

                {/* QR Code */}
                <div className="qr-display">
                    {qrDataUrl ? (
                        <img src={qrDataUrl} alt="PromptPay QR" className="qr-image" />
                    ) : (
                        <div className="qr-placeholder">
                            <span>กรอกจำนวนเงินเพื่อสร้าง QR</span>
                        </div>
                    )}
                    <div className="promptpay-details">
                        <div className="pp-name">{promptPay.name || 'ไม่ระบุชื่อ'}</div>
                        <div className="pp-id">ID: {promptPay.number}</div>
                    </div>
                </div>

                {/* Amount Display */}
                {numericAmount > 0 && (
                    <div className="amount-display">
                        {formatCurrency(numericAmount)}
                    </div>
                )}

                {/* Amount Input */}
                <Card className="input-card">
                    <Input
                        type="number"
                        placeholder="กรอกจำนวนเงิน"
                        value={amount}
                        onChange={setAmount}
                        className="amount-input"
                    />
                    <Button
                        block
                        color="primary"
                        size="large"
                        loading={saving}
                        disabled={numericAmount <= 0}
                        onClick={handleSave}
                        className="save-qr-btn"
                    >
                        บันทึกยอดขาย
                    </Button>
                </Card>
            </div>

            {/* Hidden file input for camera */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </div>
    );
}
